import { inject, Injectable } from '@angular/core';
import { MetricService } from '../../core/services/metric.service';
import { ComponentStore } from '@ngrx/component-store';
import { HeatmapApiService, HeatmapValueUniversal } from './api/heatmap-api.service';
import {delay, exhaustMap, map, of, pipe, tap, debounceTime} from 'rxjs';
import { MapboxGeoJSONFeature } from 'mapbox-gl';
import * as mapboxgl from 'mapbox-gl';
import { filter } from 'rxjs/operators';
import * as turf from '@turf/turf';

export const PaintColors = [
  '#1a5632',  // Class 1: darkest green (most below median)
  '#2d6a4f',  // Class 2: dark green
  '#40916c',  // Class 3: medium-dark green
  '#74c476',  // Class 4: light green
  '#c9e77c',  // Class 5: yellow-green
  '#f7f74a',  // Class 6: bright yellow (at median)
  '#f5a742',  // Class 7: orange
  '#e86443',  // Class 8: red-orange
  '#d63384',  // Class 9: pink
  '#9d4edd',  // Class 10: purple
  '#7b2cbf',  // Class 11: dark purple (most above median)
];

export const PaintColors2 = new Array(21).fill(null).map((it, index) => {
  return `rgba(242, 72, 34, ${0.045 * index})`;
});
export enum HeatmapMode {
  Desa = 'desa',
  Kecamatan = 'kecamatan',
  Kabupaten = 'kabupaten',
  Province = 'province',
}

export const IdKeyByHeatmapMode: Record<
  HeatmapMode,
  'admin4pcode' | 'admin3pcode' | 'admin2pcode' | 'admin1pcode' | 'off'
> = {
  [HeatmapMode.Desa]: 'admin4pcode',
  [HeatmapMode.Kecamatan]: 'admin3pcode',
  [HeatmapMode.Kabupaten]: 'admin2pcode',
  [HeatmapMode.Province]: 'admin1pcode',
};

export type HeatmapValuesState = {
  isEnabled: boolean;
  selectedHeatmapMode: HeatmapMode;
  currentPriceRules: HeatmapValueUniversal[];
  onlyShowedZone: number | null;
  map: mapboxgl.Map | null;
  zoneData: HeatmapValueUniversal | null;
};

@Injectable()
export class HeatmapValuesService extends ComponentStore<HeatmapValuesState> {
  private readonly metricService = inject(MetricService);
  private currentBatchCanceled = false;

  constructor() {
    super({
      isEnabled: false,
      selectedHeatmapMode: HeatmapMode.Desa,
      currentPriceRules: [],
      map: null,
      onlyShowedZone: null,
      zoneData: null,
    });
  }

  private recalculateValues = this.effect<void>(
    pipe(
      filter(() => this.get().isEnabled),
      debounceTime(300),
      tap(() => {
        const state = this.get();
        if (!state.map) return;

        const mode = state.selectedHeatmapMode;
        const idKey = IdKeyByHeatmapMode[mode];
        if (!idKey) return;

        // Проверяем существование слоев
        const boundaryLayerId = `plot_heatmap_ml_${mode}-line`;
        const plotLayerId = 'plot_heatmap_ml-fill';
        
        if (!state.map.getLayer(boundaryLayerId) || !state.map.getLayer(plotLayerId)) {
          console.log('Layers not found');
          return;
        }

        // Получаем видимые границы и участки
        const visibleBoundaries = state.map.queryRenderedFeatures(
          undefined,
          { layers: [boundaryLayerId] }
        );

        const visiblePlots = state.map.queryRenderedFeatures(
          undefined,
          { layers: [plotLayerId] }
        );

        this.processFeatures(visibleBoundaries, visiblePlots, mode);
      }),
    ),
  );

  private processFeatures(
    boundaries: MapboxGeoJSONFeature[],
    plots: MapboxGeoJSONFeature[],
    mode: HeatmapMode
  ) {
    const state = this.get();
    if (!state.map) return;

    const idKey = IdKeyByHeatmapMode[mode];
    if (!idKey) return;

    // Находим доминирующую зону
    const bounds = state.map.getBounds();
    if (!bounds) return;

    const viewportBbox: turf.BBox = [
      bounds.getWest(),
      bounds.getSouth(),
      bounds.getEast(),
      bounds.getNorth()
    ];
    const viewportPolygon = turf.bboxPolygon(viewportBbox);

    let maxArea = 0;
    let maxZoneId = 0;
    let maxZoneBoundary = null;

    // Находим самую большую видимую зону
    for (const boundary of boundaries) {
      const zoneId = boundary.properties?.[idKey];
      if (!zoneId || boundary.geometry?.type !== 'Polygon') continue;

      const polygonFeature = turf.polygon((boundary.geometry as any).coordinates);
      const intersection = turf.intersect(polygonFeature, viewportPolygon);
      
      if (intersection) {
        const area = turf.area(intersection);
        if (area > maxArea) {
          maxArea = area;
          maxZoneId = Number(zoneId);
          maxZoneBoundary = boundary;
        }
      }
    }

    if (!maxZoneId || !maxZoneBoundary) return;
    // Создаем правило на основе данных из boundary
    const zoneRule: HeatmapValueUniversal = {
      admincode: maxZoneBoundary.properties?.[idKey],
      min: maxZoneBoundary.properties?.['min'] || 0,
      max: maxZoneBoundary.properties?.['max'] || 0,
      avg: maxZoneBoundary.properties?.['avg'] || 0,
      type: maxZoneBoundary.properties?.['type'] || 0,
      count: maxZoneBoundary.properties?.['count'] || 0,
      median: maxZoneBoundary.properties?.['median'] || 0,
      name: maxZoneBoundary.properties?.['name'] || '',
    };

    // Обновляем состояния участков
    const featureStateUpdates: Array<{
      feature: MapboxGeoJSONFeature;
      state: { [key: string]: any };
    }> = [];

    plots.forEach(plot => {
      if (plot.properties?.[idKey] === maxZoneId) {
        const predictValue = plot.properties?.['predict'] || 0;
        const newColor = getColor(
          zoneRule.min,
          zoneRule.max,
          predictValue,
          PaintColors
        );

        const featureStateKeyPaintKey = 'heat-fill-color' + mode;
        const currentColor = plot.state?.[featureStateKeyPaintKey];
        const currentMode = plot.state?.['heat-fill-color-by'];

        if (currentColor !== newColor || currentMode !== mode) {
          featureStateUpdates.push({
            feature: plot,
            state: {
              [featureStateKeyPaintKey]: newColor,
              'heat-fill-color-by': mode,
            }
          });
        }
      }
    });

    if (featureStateUpdates.length > 0) {
      featureStateUpdates.forEach(({ feature, state }) => {
        this.get()?.map?.setFeatureState(feature, state);
      });
    }

    this.patchState({
      currentPriceRules: [zoneRule],
      onlyShowedZone: maxZoneId,
      zoneData: zoneRule,
    });
  }

  public setMap(map: mapboxgl.Map) {
    this.patchState({ map });

    map.on('movestart', () => {
      this.currentBatchCanceled = true;
    });

    map.on('moveend', () => {
      this.currentBatchCanceled = false;
      this.recalculateValues();
    });

    map.on('sourcedata', (e) => {
      const state = this.get();
      if (!state.isEnabled) return;

      if (
        e.sourceId === 'plot_heatmap_ml' && 
        e.isSourceLoaded && 
        map.getSource('plot_heatmap_ml')?.loaded()
      ) {
        this.currentBatchCanceled = false;
        this.recalculateValues();
      }
    });
  }

  public setHeatmapMode = this.effect<HeatmapMode>((value$) => {
    return value$.pipe(
      tap((value) => {
        this.patchState({
          selectedHeatmapMode: value,
          zoneData: null
        });
      }),
      delay(200),
      tap(() => {
        this.recalculateValues();
      }),
    );
  });

  public clearMap() {
    this.patchState({ 
      map: null, 
      isEnabled: false,
      zoneData: null
    });
  }

  public setIsEnabled(isEnabled: boolean) {
    this.patchState({
      isEnabled,
    });
    if (isEnabled) {
      this.metricService.track({ name: 'heatmap_toggle' });
    }

    setTimeout(() => {
      this.recalculateValues();
    }, 200);
  }
}

function getColor(min: number, max: number, predict: number, paintColors: string[]): string {
  if (paintColors.length !== 11) {
    throw new Error('PaintColors array must contain exactly 11 elements.');
  }

  if (min > max) {
    throw new Error('Min value cannot be greater than max value.');
  }

  if (predict < min || predict > max) {
    predict = max;
  }

  // Разделяем диапазон между min и max на 10 интервалов (для 11 цветов)
  const intervalSize = (max - min) / 10;

  // Вычисляем индекс цвета на основе цены
  const index = Math.min(
    Math.floor((predict - min) / intervalSize),
    paintColors.length - 1, // Убеждаемся, что индекс не выходит за пределы массива
  );

  return paintColors[index];
}
